import { useTranslations } from '@vocab/react';
import {
  Badge,
  Bleed,
  Box,
  Column,
  Columns,
  Hidden,
  Heading,
  HiddenVisually,
  Inline,
  List,
  Stack,
  Text,
  useResponsiveValue,
  Link,
  TextLink,
} from 'braid-design-system';
import type { ComponentProps, ReactNode } from 'react';
import translations from './.vocab';
import { getJobDataAutomation, highlightFromHTML } from './helpers';
import * as styles from './JobListItemView.css';
import { LazyLoadedImg } from './LazyLoadedImg/LazyLoadedImg';
import MetaLink from './MetaLink/MetaLink.tsx';
import SaveJob, { type SaveJobProps } from './SaveJob/SaveJob.tsx';

export interface JobCardLinkProps {
  href?: string;
  rel?: string;
  target?: string;
  title?: string;
  name: string;
}

export interface JobDetailPageLink extends Omit<JobCardLinkProps, 'name'> {
  onBeforeNavigation?: () => void;
}

export interface JobListItemViewProps
  extends Pick<ComponentProps<typeof Box>, 'aria-label' | 'onClick'> {
  bulletPoints: string[] | null;
  id: number;
  isAuthenticated: boolean;
  isPremium?: boolean;
  isSelected?: boolean;
  isStandOut?: boolean;
  isSplitView: boolean;
  isShowJobCardTeaserInSplitView?: boolean;
  jobDetailPageLink: JobDetailPageLink;
  jobDetailPageJobCardOriginLink: Omit<JobDetailPageLink, 'onBeforeNavigation'>;
  jobDetailPageCardTitleOriginLink: Omit<
    JobDetailPageLink,
    'onBeforeNavigation'
  >;
  joraImpressionTrackingUrl?: string;
  listingDate?: string;
  listingDateDescription?: string;
  locationMatch?: 'Exact';
  salaryDisplayLabel?: string;
  srcLogo?: string;
  salary: string;
  shouldAddFocusEffect?: boolean;
  shouldAddHoverEffect?: boolean;
  shouldAddOverlayLink?: boolean;
  shouldOpenApplyInNewTab: boolean;
  saveJob: Pick<
    SaveJobProps,
    'isApplied' | 'isSaved' | 'returnUrl' | 'toggleSavedJob'
  >;
  jobTitle: string | ReactNode;
  jobLinkOnClick: (e: React.MouseEvent) => void;
  jobMetaLinkOnClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  teaser: string;
  companyLink: JobCardLinkProps;
  locationLinks?: JobCardLinkProps[];
  classificationLinks: JobCardLinkProps[];
  workType: string;
}

export const generateJobTitleLinkId = (id: string | number) =>
  `job-title-${id}`;

const ClickThroughOverlay = ({ children }: { children: ReactNode }) => (
  <Box position={{ desktop: 'relative' }} display="inline">
    {children}
  </Box>
);

const premiumAdBadgeAutomation = 'premiumAdBadge';
const jobListingDateAutomation = 'jobListingDate';

const JobListItemView = ({
  'aria-label': ariaLabel,
  companyLink,
  classificationLinks,
  bulletPoints,
  id,
  isAuthenticated,
  isPremium = false,
  isSelected = false,
  isShowJobCardTeaserInSplitView = false,
  isStandOut = false,
  isSplitView,
  jobDetailPageLink,
  jobDetailPageJobCardOriginLink,
  jobDetailPageCardTitleOriginLink,
  jobLinkOnClick,
  jobMetaLinkOnClick,
  jobTitle: title,
  joraImpressionTrackingUrl,
  listingDate,
  listingDateDescription,
  locationLinks,
  locationMatch,
  srcLogo,
  onClick,
  salaryDisplayLabel,
  shouldAddHoverEffect = false,
  shouldAddFocusEffect = false,
  shouldOpenApplyInNewTab,
  saveJob: { isApplied, isSaved, returnUrl, toggleSavedJob },
  teaser,
  workType,
}: JobListItemViewProps) => {
  const { t } = useTranslations(translations);
  const shouldRenderLogo = srcLogo !== undefined;
  const shouldOpenInNewTab = shouldOpenApplyInNewTab && isSplitView;

  const isMobile = useResponsiveValue()({
    mobile: true,
    desktop: false,
  });

  const showJobcardTeaserInSplitView =
    !isSplitView ||
    (isSplitView && (isShowJobCardTeaserInSplitView || isMobile));

  const premiumBadgeText = t('Featured');
  const premiumBadgeDescriptionText = t('This is a featured job');

  const overlaySaveJob = (
    <ClickThroughOverlay>
      <SaveJob
        isApplied={isApplied}
        isSaved={isSaved}
        returnUrl={returnUrl}
        toggleSavedJob={toggleSavedJob}
        isAuthenticated={isAuthenticated}
        isSplitView={isSplitView}
        jobId={String(id)}
      />
    </ClickThroughOverlay>
  );

  return (
    <Box
      component="article"
      cursor={isSplitView ? 'pointer' : 'default'}
      className={
        isSplitView
          ? [
              styles.splitView.jobCardBase,
              {
                [styles.splitView.selectedJobCard]: isSelected,
                [styles.splitView.hoveredJobCard]: shouldAddHoverEffect,
                [styles.splitView.focusedJobCard]: shouldAddFocusEffect,
              },
            ]
          : [
              styles.root,
              {
                [styles.root_isStandOut]: !isPremium && isStandOut,
                [styles.root_isPremium]: isPremium,
              },
            ]
      }
      padding="gutter"
      background={isSplitView ? 'surface' : undefined}
      // SplitView's boxShadow is handled in CSS within splitView class names
      boxShadow={!isSplitView ? 'borderNeutralLight' : undefined}
      borderRadius="large"
      aria-label={ariaLabel}
      aria-selected={isSelected}
      data-card-type="JobCard"
      data-automation={getJobDataAutomation(isPremium, locationMatch)}
      data-job-id={id}
      position="relative"
      onClick={onClick}
    >
      <Hidden above="tablet">
        <Link
          href={jobDetailPageLink.href || ''}
          title={jobDetailPageLink.title}
          rel={jobDetailPageLink.rel}
          className={styles.tappableJob}
          data-automation="job-list-view-job-link"
          target={shouldOpenInNewTab ? '_blank' : '_self'}
          onClick={jobLinkOnClick}
        />
      </Hidden>

      {isSplitView && (
        <Hidden below="desktop">
          <Link
            href={jobDetailPageJobCardOriginLink.href || ''}
            title={jobDetailPageJobCardOriginLink.title}
            rel={jobDetailPageJobCardOriginLink.rel}
            className={[styles.tappableJob, styles.outlinedJob]}
            data-automation="job-list-item-link-overlay"
            data-run-click-only={true}
            onClick={jobLinkOnClick}
          />
        </Hidden>
      )}

      {joraImpressionTrackingUrl && (
        <Box
          component="img"
          src={joraImpressionTrackingUrl}
          className={styles.trackingPixel}
        />
      )}

      {isPremium ? (
        <HiddenVisually>{premiumBadgeDescriptionText}</HiddenVisually>
      ) : (
        listingDateDescription && (
          <HiddenVisually>{listingDateDescription}</HiddenVisually>
        )
      )}

      <Stack space={!isSplitView && !shouldRenderLogo ? 'large' : 'small'}>
        <Stack space="medium">
          <Columns space="small">
            <Column>
              {isSplitView ? (
                <Stack space={shouldRenderLogo ? 'xsmall' : 'medium'}>
                  {(isPremium || shouldRenderLogo) && (
                    <Stack space="small">
                      {isPremium && (
                        <Box
                          position="relative"
                          className={styles.splitView.featuredBadge}
                        >
                          <Badge
                            tone="promote"
                            bleedY
                            data={{ automation: premiumAdBadgeAutomation }}
                          >
                            {premiumBadgeText}
                          </Badge>
                        </Box>
                      )}
                      {shouldRenderLogo && (
                        <LazyLoadedImg
                          src={srcLogo}
                          dataAutomation="company-logo"
                        />
                      )}
                    </Stack>
                  )}
                  <Stack space="small">
                    <Text weight="strong" component="h3">
                      <ClickThroughOverlay>
                        <Link
                          href={jobDetailPageCardTitleOriginLink.href || ''}
                          title={jobDetailPageCardTitleOriginLink.title}
                          rel={jobDetailPageCardTitleOriginLink.rel}
                          className={{
                            [styles.showVisited]: !isSelected,
                            [styles.showHovered]: shouldAddHoverEffect,
                          }}
                          data-automation="jobTitle"
                          data-allow-propagation={!isSplitView}
                          data-run-click-only={true}
                          id={generateJobTitleLinkId(id)}
                          onClick={jobLinkOnClick}
                        >
                          {title}
                        </Link>
                      </ClickThroughOverlay>
                    </Text>
                    <Text>
                      <ClickThroughOverlay>
                        <HiddenVisually>{t('at')} </HiddenVisually>
                        <MetaLink
                          {...companyLink}
                          type="company"
                          isSplitView={isSplitView}
                          onClick={jobMetaLinkOnClick}
                        />
                      </ClickThroughOverlay>
                    </Text>
                  </Stack>
                </Stack>
              ) : (
                <Stack space="small">
                  <Heading level="4" component="h3">
                    <TextLink
                      href={jobDetailPageLink.href || ''}
                      title={jobDetailPageLink.title}
                      rel={jobDetailPageLink.rel}
                      showVisited
                      data-automation="jobTitle"
                      id={generateJobTitleLinkId(id)}
                      onClick={jobDetailPageLink.onBeforeNavigation}
                    >
                      {title}
                    </TextLink>
                  </Heading>
                  <Text>
                    <HiddenVisually>{t('at')} </HiddenVisually>
                    <MetaLink
                      {...companyLink}
                      type="company"
                      isSplitView={isSplitView}
                      onClick={jobMetaLinkOnClick}
                    />
                  </Text>
                </Stack>
              )}
            </Column>
            <Column width="content">
              {isSplitView ? (
                overlaySaveJob
              ) : (
                <Box aria-hidden="true">
                  {isPremium ? (
                    <Badge
                      tone="promote"
                      bleedY
                      data={{ automation: premiumAdBadgeAutomation }}
                    >
                      {premiumBadgeText}
                    </Badge>
                  ) : (
                    <Text
                      size="small"
                      tone="secondary"
                      data={{ automation: jobListingDateAutomation }}
                    >
                      {listingDate}
                    </Text>
                  )}
                </Box>
              )}
            </Column>
          </Columns>

          <Box>
            <HiddenVisually>
              <Box component="p">
                {t('This is a {workType} job', { workType })}
              </Box>
            </HiddenVisually>
            <Stack space="xsmall">
              <Box>
                <Text size="small">
                  {locationLinks?.map((props, index) => (
                    // the locations will be split by comma and space
                    // e.g. "Lewisham, Sydney NSW"
                    <Box component="span" key={index}>
                      {index % 2 !== 0 && ', '}
                      <MetaLink
                        {...props}
                        type="location"
                        isSplitView
                        disabledLink
                        onClick={jobMetaLinkOnClick}
                      />
                    </Box>
                  ))}
                </Text>
              </Box>
              {salaryDisplayLabel ? (
                <Box aria-label={`Salary: ${salaryDisplayLabel}`}>
                  <Text
                    size="small"
                    data={{ automation: 'jobSalary' }}
                    maxLines={2}
                  >
                    {salaryDisplayLabel}
                  </Text>
                </Box>
              ) : null}

              <Hidden below="tablet">
                <Text size="small">
                  <Inline space="none">
                    {classificationLinks[1] ? (
                      <>
                        <HiddenVisually>{`subClassification: ${classificationLinks[1].name}`}</HiddenVisually>
                        <MetaLink
                          {...classificationLinks[1]}
                          type="subClassification"
                          isSplitView
                          disabledLink
                          onClick={jobMetaLinkOnClick}
                        />
                        <Box marginLeft="xxsmall" />
                        <HiddenVisually>{`classification: ${classificationLinks[0].name}`}</HiddenVisually>
                        <MetaLink
                          {...classificationLinks[0]}
                          type="classification"
                          isChild={true}
                          isSplitView
                          disabledLink
                          onClick={jobMetaLinkOnClick}
                        />
                      </>
                    ) : (
                      <>
                        <HiddenVisually>{`classification: ${classificationLinks[0].name}`}</HiddenVisually>
                        <MetaLink
                          {...classificationLinks[0]}
                          type="classification"
                          isSplitView
                          disabledLink
                          onClick={jobMetaLinkOnClick}
                        />
                      </>
                    )}
                  </Inline>
                </Text>
              </Hidden>
            </Stack>
          </Box>

          {bulletPoints?.length ? (
            <List size="small" space="xsmall">
              {bulletPoints.map((bulletPoint, i) => (
                <Text key={i} size="small">
                  {bulletPoint}
                </Text>
              ))}
            </List>
          ) : null}

          {showJobcardTeaserInSplitView ? (
            <Text
              size="small"
              tone="secondary"
              data={{ automation: 'jobShortDescription' }}
            >
              {highlightFromHTML({ html: teaser, isDescription: true })}
            </Text>
          ) : null}

          {isSplitView && !isPremium && listingDate ? (
            <Text
              size="small"
              tone="secondary"
              data={{ automation: jobListingDateAutomation }}
            >
              {listingDate}
            </Text>
          ) : null}
        </Stack>

        {!isSplitView && (
          <Columns space="none" alignY="bottom">
            <Column>
              <Inline space="none">{overlaySaveJob}</Inline>
            </Column>
            {shouldRenderLogo ? (
              <Column width="content">
                <Bleed bottom="xsmall">
                  <LazyLoadedImg src={srcLogo} dataAutomation="company-logo" />
                </Bleed>
              </Column>
            ) : null}
          </Columns>
        )}
      </Stack>
    </Box>
  );
};

export default JobListItemView;
